import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { Formik, Form, Field, ErrorMessage, useFormikContext } from "formik";
import { Card, CardBody, Col, Container, FormGroup, Input, Label, Row, CardHeader, CardFooter, Button } from "reactstrap";
import Breadcrumbs from "../../common-elements/breadcrumbs/Breadcrumbs"; // Adjust path
import { H3, P, Image, H5 } from "../../AbstractElements"; // Adjust path
import { dynamicImage } from "../../service"; // Adjust path
import ButtonSection from "../organization/create-organization/ButtonSection";
import { Link, useNavigate } from "react-router-dom";



// 1. Define the shape of your profile data for TypeScript
interface ProfileData {
  name: string;
  role: string;
  avatar: string | File; // Can be a string path or a new File object
  email: string;
  phone: string;
  gender: string;
  accountType: string;
  status: string;
  city: string;
  address: string;
  isEmailVerified: boolean;
  isPhoneVerified: boolean;
}

// 2. Define validation schema with Yup
const validationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  email: Yup.string().email("Invalid email address").required("Email is required"),
  phone: Yup.string().matches(/^[0-9]+$/, "Must be only digits").min(10, 'Must be at least 10 digits').required("Phone is required"),
  role: Yup.string().required("Role is required"),
  gender: Yup.string().required("Gender is required"),
});


// 3. Create a dedicated component for the Image Upload logic
const ProfileImageUpload = () => {
  const { values, setFieldValue } = useFormikContext<ProfileData>();
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
        setFieldValue('avatar', file); // Update Formik state
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Col xs="auto">
      <div style={{ position: "relative" }}>
        {/* The image preview uses the photoPreview state, falling back to the Formik value */}
        <Image
          className="img-100 rounded-circle border border-3"
          alt="edit-user"
          src={photoPreview || (typeof values.avatar === 'string' ? dynamicImage(values.avatar) : '')}
        />
        <button
          type="button"
          className="btn btn-light position-absolute bottom-0 end-0 p-1 border shadow rounded-circle"
          style={{ lineHeight: 1 }}
          onClick={() => fileInputRef.current?.click()}
          title="Change Photo"
        >
          <i className="fa fa-camera text-primary" />
        </button>
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handlePhotoChange}
        />
      </div>
    </Col>
  );
};


function EditProfilePage() {
  const { t } = useTranslation();
  const navigate=useNavigate()
  // 4. Set initial values for the form (could be fetched from an API)
  const initialValues: ProfileData = {
    name: "Haris",
    role: "Lead Software Architect",
    avatar: "dashboard-8/user3.png",
    email: "Abc@gmail.com",
    phone: "8797654323",
    gender: "Male",
    accountType: "Premium",
    status: "Active",
    city: "New York",
    address: "123 Main St, Anytown USA",
    isEmailVerified: true,
    isPhoneVerified: false,
  };

  const GENDER_OPTIONS = ["Male", "Female", "Other"];
  const ROLE_OPTIONS = ["Lead Software Architect", "Designer", "Developer"];
  const ACCOUNT_TYPE_OPTIONS = ["Premium", "Standard", "Basic"];
  const STATUS_OPTIONS = ["Active", "Inactive", "Suspended"];

  // 5. Handle Form Submission
  const handleSubmit = (values: ProfileData, { setSubmitting }: any) => {
    console.log("Formik Submitted Data:", values);
    // Simulate API call
    setTimeout(() => {
      alert("Profile saved successfully! Check the console.");
      setSubmitting(false);
    }, 1000);
  };
  const handleClose=()=>{
    navigate("profile/Account")
  }

  return (
    <div>
      <Breadcrumbs
        mainTitle={t("Profile")}
        parent={t("Account")}
        parentLink={`${process.env.PUBLIC_URL}/profile/account`}
      />
      <Container fluid>
        <Row>
          <Col xl={12}>
            {/* 6. Wrap the entire form with the Formik component */}
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ isSubmitting, values }) => (
                <Form className="theme-form">
                  <Card className="mt-4 shadow-sm">
                    <CardHeader>
                      <H5>{t("Profile Settings")}</H5>
                      <span>{t("Update your personal details here.")}</span>
                    </CardHeader>
                    <CardBody>
                      <Row className="mb-4 align-items-center">
                        <ProfileImageUpload />
                        <Col className="d-flex flex-column justify-content-center">
                          <H3 className="mb-1">{values.name}</H3>
                          <P className="mb-0 text-muted">{values.role}</P>
                        </Col>
                      </Row>

                      {/* 7. Replace Inputs with Formik's <Field> and <ErrorMessage> */}
                      <Row>
                        <Col md={4} className="mb-3">
                          <FormGroup>
                            <Label for="name">{t("Name")} <span className="txt-danger">*</span></Label>
                            <Field id="name" name="name" type="text" className="form-control" />
                            <ErrorMessage name="name" component="span" className="text-danger" />
                          </FormGroup>
                        </Col>
                        <Col md={4} className="mb-3">
                          <FormGroup>
                            <Label for="role">{t("Role")} <span className="txt-danger">*</span></Label>
                            <Field as="select" id="role" name="role" className="form-control">
                              {ROLE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </Field>
                            <ErrorMessage name="role" component="span" className="text-danger" />
                          </FormGroup>
                        </Col>
                        <Col md={4} className="mb-3">
                          <FormGroup>
                            <Label for="email">{t("Email")} <span className="txt-danger">*</span></Label>
                            <Field id="email" name="email" type="email" className="form-control" />
                            <ErrorMessage name="email" component="span" className="text-danger" />
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={4} className="mb-3">
                          <FormGroup>
                            <Label for="phone">{t("Phone")} <span className="txt-danger">*</span></Label>
                            <Field id="phone" name="phone" type="tel" className="form-control" />
                            <ErrorMessage name="phone" component="span" className="text-danger" />
                          </FormGroup>
                        </Col>
                        <Col md={4} className="mb-3">
                          <FormGroup>
                            <Label for="accountType">{t("AccountType")}</Label>
                            <Field as="select" id="accountType" name="accountType" className="form-control">
                              {ACCOUNT_TYPE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </Field>
                          </FormGroup>
                        </Col>
                        <Col md={4} className="mb-3">
                          <FormGroup>
                            <Label for="status">{t("Status")}</Label>
                            <Field as="select" id="status" name="status" className="form-control">
                              {STATUS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </Field>
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={4} className="mb-3">
                          <FormGroup>
                            <Label for="gender">{t("Gender")} <span className="txt-danger">*</span></Label>
                            <Field as="select" id="gender" name="gender" className="form-control">
                              {GENDER_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </Field>
                            <ErrorMessage name="gender" component="span" className="text-danger" />
                          </FormGroup>
                        </Col>
                        <Col md={4} className="mb-3">
                          <FormGroup>
                            <Label for="city">{t("City")}</Label>
                            <Field id="city" name="city" type="text" className="form-control" />
                          </FormGroup>
                        </Col>
                        <Col md={4} className="mb-3">
                          <FormGroup>
                            <Label for="address">{t("Address")}</Label>
                            <Field id="address" name="address" type="text" className="form-control" />
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={4} className="mb-3">
                          <FormGroup check className="d-flex gap-2">
                            <Field type="checkbox" id="isEmailVerified" name="isEmailVerified" className="form-check-input" />
                            <Label for="isEmailVerified" check>{t("EmailVerified")}</Label>
                          </FormGroup>
                        </Col>
                        <Col md={4} className="mb-3">
                          <FormGroup check className="d-flex gap-2">
                            <Field type="checkbox" id="isPhoneVerified" name="isPhoneVerified" className="form-check-input" />
                            <Label for="isPhoneVerified" check>{t("PhoneVerified")}</Label>
                          </FormGroup>
                        </Col>
                      </Row>
                    </CardBody>
                    
                    <CardFooter className="text-end  border-0 p-3">
                      <Link to={`${process.env.PUBLIC_URL}/profile/account`}><Button color="secondary"  className="me-2">{t("Cancel")}</Button></Link>
                      <Button color="primary" type="submit">{t("Save")}</Button>
                    </CardFooter>
                  </Card>
                </Form>
              )}
            </Formik>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default EditProfilePage;